import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  type DigitStyle,
  type FontKey,
  type FrameStyle,
  type LayoutBackground,
  type LayoutBinding,
  type LayoutTheme,
  type LayoutZone,
  type ThemePreset,
  type WidgetKind,
} from '../api';
import { AppShell } from '../components/AppShell';

const WIDGET_KINDS: { kind: WidgetKind; label: string; teamScoped: boolean }[] = [
  { kind: 'score', label: 'Score', teamScoped: true },
  { kind: 'gameClock', label: 'Game clock', teamScoped: false },
  { kind: 'shotClock', label: 'Shot clock', teamScoped: false },
  { kind: 'period', label: 'Period', teamScoped: false },
  { kind: 'teamName', label: 'Team name', teamScoped: true },
  { kind: 'teamLogo', label: 'Team logo', teamScoped: true },
  { kind: 'possession', label: 'Possession', teamScoped: true },
  { kind: 'bonus', label: 'Bonus', teamScoped: true },
  { kind: 'timeouts', label: 'Timeouts', teamScoped: true },
  { kind: 'teamFouls', label: 'Team fouls', teamScoped: true },
  { kind: 'playerStatsTable', label: 'Player stats', teamScoped: true },
  { kind: 'teamStatsSummary', label: 'Team stats', teamScoped: true },
  { kind: 'mediaZone', label: 'Media zone', teamScoped: false },
  { kind: 'sponsorBanner', label: 'Sponsor', teamScoped: false },
  { kind: 'messageBanner', label: 'Message', teamScoped: false },
];

function kindLabel(k?: WidgetKind): string {
  return WIDGET_KINDS.find((w) => w.kind === k)?.label ?? '—';
}
const snap = (n: number) => Math.round(n);

let zoneSeq = 0;

export function LayoutBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState('');
  const [zones, setZones] = useState<LayoutZone[]>([]);
  const [bindings, setBindings] = useState<Record<string, LayoutBinding>>({});
  const [background, setBackground] = useState<LayoutBackground | null>(null);
  const [theme, setTheme] = useState<LayoutTheme | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!id) return;
    api.listLayouts().then((list) => {
      const l = list.find((x) => x.id === id);
      if (l) {
        setName(l.name);
        setZones(l.zones);
        setBindings(l.bindings);
        setBackground(l.background ?? null);
        setTheme(l.theme ?? null);
      }
      setLoaded(true);
    });
  }, [id]);

  const sel = zones.find((z) => z.id === selected) ?? null;

  function updateZone(zid: string, patch: Partial<LayoutZone>) {
    setZones((zs) => zs.map((z) => (z.id === zid ? { ...z, ...patch } : z)));
  }
  function setBinding(zid: string, patch: Partial<LayoutBinding>) {
    setBindings((b) => {
      const cur: LayoutBinding = b[zid] ?? { kind: 'score' };
      return { ...b, [zid]: { ...cur, ...patch } };
    });
  }

  function addZone() {
    const zid = `zone-${Date.now()}-${zoneSeq++}`;
    setZones((zs) => [
      ...zs,
      { id: zid, type: 'composite', x: 35, y: 40, w: 30, h: 20 },
    ]);
    setBindings((b) => ({ ...b, [zid]: { kind: 'score', side: 'home' } }));
    setSelected(zid);
  }
  function deleteZone(zid: string) {
    setZones((zs) => zs.filter((z) => z.id !== zid));
    setBindings((b) => {
      const next = { ...b };
      delete next[zid];
      return next;
    });
    if (selected === zid) setSelected(null);
  }

  // ─── Pointer drag / resize ───
  const drag = useRef<{
    zid: string;
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    z0: LayoutZone;
  } | null>(null);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!d || !rect) return;
    const dxPct = ((e.clientX - d.startX) / rect.width) * 100;
    const dyPct = ((e.clientY - d.startY) / rect.height) * 100;
    if (d.mode === 'move') {
      const x = Math.max(0, Math.min(100 - d.z0.w, snap(d.z0.x + dxPct)));
      const y = Math.max(0, Math.min(100 - d.z0.h, snap(d.z0.y + dyPct)));
      updateZone(d.zid, { x, y });
    } else {
      const w = Math.max(5, Math.min(100 - d.z0.x, snap(d.z0.w + dxPct)));
      const h = Math.max(5, Math.min(100 - d.z0.y, snap(d.z0.h + dyPct)));
      updateZone(d.zid, { w, h });
    }
  }, []);

  const onPointerUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  function startDrag(
    e: React.PointerEvent,
    z: LayoutZone,
    mode: 'move' | 'resize',
  ) {
    e.stopPropagation();
    setSelected(z.id);
    drag.current = {
      zid: z.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      z0: { ...z },
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }

  async function save() {
    if (!id) return;
    setSaving(true);
    try {
      await api.updateLayout(id, { name, zones, bindings, background, theme });
      setSavedAt(new Date().toLocaleTimeString());
    } finally {
      setSaving(false);
    }
  }

  const displayOrigin =
    typeof window !== 'undefined'
      ? window.location.origin.replace('app.', 'display.')
      : '';

  if (!loaded) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-3">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Link to="/layouts" className="text-sm text-muted hover:text-text">
              ← Layouts
            </Link>
            <input
              className="input w-56"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Layout name"
            />
          </div>
          <div className="flex items-center gap-2">
            {savedAt && (
              <span className="text-xs text-muted">saved {savedAt}</span>
            )}
            <a
              className="btn btn-ghost text-sm"
              href={`${displayOrigin}/preview-engine?layout=${id}`}
              target="_blank"
              rel="noreferrer"
            >
              Preview ↗
            </a>
            <button className="btn btn-secondary" onClick={addZone}>
              + Zone
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_280px]">
          {/* Canvas */}
          <div
            ref={canvasRef}
            onPointerDown={() => setSelected(null)}
            className="relative w-full select-none overflow-hidden rounded-lg border border-border"
            style={{ aspectRatio: '16 / 9', ...canvasBgStyle(background) }}
          >
            {background?.type === 'image' && background.imageUrl && (
              <>
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${background.imageUrl})`,
                    backgroundSize: background.fit ?? 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                {(background.overlay ?? 0) > 0 && (
                  <div
                    className="absolute inset-0"
                    style={{ background: `rgba(0,0,0,${background.overlay})` }}
                  />
                )}
              </>
            )}
            {zones.map((z) => {
              const b = bindings[z.id];
              const isSel = selected === z.id;
              return (
                <div
                  key={z.id}
                  onPointerDown={(e) => startDrag(e, z, 'move')}
                  className={`absolute flex cursor-move items-center justify-center rounded-sm border text-center ${
                    isSel
                      ? 'border-brand bg-brand/20 ring-1 ring-brand'
                      : 'border-white/20 bg-white/5 hover:border-brand/50'
                  }`}
                  style={{
                    left: `${z.x}%`,
                    top: `${z.y}%`,
                    width: `${z.w}%`,
                    height: `${z.h}%`,
                  }}
                >
                  <span className="pointer-events-none px-1 text-[10px] font-semibold text-text">
                    {kindLabel(b?.kind)}
                    {b?.side ? ` · ${b.side}` : ''}
                  </span>
                  {/* resize handle */}
                  <span
                    onPointerDown={(e) => startDrag(e, z, 'resize')}
                    className="absolute bottom-0 right-0 h-3 w-3 cursor-se-resize bg-brand"
                  />
                </div>
              );
            })}
            {zones.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted">
                Click “+ Zone” to add your first widget.
              </div>
            )}
          </div>

          {/* Properties panel */}
          <div className="card space-y-3">
            {!sel ? (
              <>
                <BackgroundEditor
                  background={background}
                  onChange={setBackground}
                />
                <div className="border-t border-border pt-3">
                  <ThemeEditor theme={theme} onChange={setTheme} />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-muted">
                    Zone
                  </div>
                  <button
                    onClick={() => deleteZone(sel.id)}
                    className="text-xs text-red-300 hover:underline"
                  >
                    Delete
                  </button>
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                    Widget
                  </span>
                  <select
                    className="input"
                    value={bindings[sel.id]?.kind ?? 'score'}
                    onChange={(e) =>
                      setBinding(sel.id, { kind: e.target.value as WidgetKind })
                    }
                  >
                    {WIDGET_KINDS.map((w) => (
                      <option key={w.kind} value={w.kind}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                    Team
                  </span>
                  <select
                    className="input"
                    value={bindings[sel.id]?.side ?? ''}
                    onChange={(e) =>
                      setBinding(sel.id, {
                        side:
                          e.target.value === ''
                            ? undefined
                            : (e.target.value as 'home' | 'away'),
                      })
                    }
                  >
                    <option value="">— none —</option>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </label>

                {bindings[sel.id]?.kind === 'messageBanner' && (
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                      Message text
                    </span>
                    <input
                      className="input"
                      value={
                        (bindings[sel.id]?.options?.text as string) ?? ''
                      }
                      onChange={(e) =>
                        setBinding(sel.id, {
                          options: {
                            ...bindings[sel.id]?.options,
                            text: e.target.value,
                          },
                        })
                      }
                    />
                  </label>
                )}

                <div className="grid grid-cols-2 gap-2">
                  {(['x', 'y', 'w', 'h'] as const).map((k) => (
                    <label key={k} className="block">
                      <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                        {k.toUpperCase()} %
                      </span>
                      <input
                        type="number"
                        className="input"
                        value={sel[k]}
                        onChange={(e) =>
                          updateZone(sel.id, {
                            [k]: Math.max(
                              0,
                              Math.min(100, parseInt(e.target.value, 10) || 0),
                            ),
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-xs text-muted">
          Drag a zone to move it; drag the bottom-right corner to resize.
          Assign a display this layout from its settings, or hit Preview to
          see it render with sample data.
        </p>
      </div>
    </AppShell>
  );
}

// Canvas base fill (theme/solid/gradient). The image layer is rendered
// separately on top of this.
function canvasBgStyle(bg: LayoutBackground | null): React.CSSProperties {
  if (bg?.type === 'solid' && bg.color) return { background: bg.color };
  if (bg?.type === 'gradient' && bg.from && bg.to) {
    return { background: `linear-gradient(135deg, ${bg.from}, ${bg.to})` };
  }
  return { background: '#000' };
}

function BackgroundEditor({
  background,
  onChange,
}: {
  background: LayoutBackground | null;
  onChange: (b: LayoutBackground | null) => void;
}) {
  const type = background?.type ?? 'theme';
  function patch(p: Partial<LayoutBackground>) {
    onChange({ type, ...background, ...p } as LayoutBackground);
  }
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wider text-muted">
        Background
      </div>
      <label className="block">
        <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
          Type
        </span>
        <select
          className="input"
          value={type}
          onChange={(e) =>
            onChange(
              e.target.value === 'theme'
                ? null
                : ({ ...background, type: e.target.value } as LayoutBackground),
            )
          }
        >
          <option value="theme">Theme color</option>
          <option value="solid">Solid color</option>
          <option value="gradient">Gradient</option>
          <option value="image">Image</option>
        </select>
      </label>

      {type === 'solid' && (
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
            Color
          </span>
          <input
            type="color"
            className="h-10 w-full rounded border border-border bg-surface"
            value={background?.color ?? '#101826'}
            onChange={(e) => patch({ color: e.target.value })}
          />
        </label>
      )}

      {type === 'gradient' && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
              From
            </span>
            <input
              type="color"
              className="h-10 w-full rounded border border-border bg-surface"
              value={background?.from ?? '#1d4ed8'}
              onChange={(e) => patch({ from: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
              To
            </span>
            <input
              type="color"
              className="h-10 w-full rounded border border-border bg-surface"
              value={background?.to ?? '#0a0e1a'}
              onChange={(e) => patch({ to: e.target.value })}
            />
          </label>
        </div>
      )}

      {type === 'image' && (
        <>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
              Image URL
            </span>
            <input
              className="input"
              type="url"
              placeholder="https://…"
              value={background?.imageUrl ?? ''}
              onChange={(e) => patch({ imageUrl: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
              Fit
            </span>
            <select
              className="input"
              value={background?.fit ?? 'cover'}
              onChange={(e) =>
                patch({ fit: e.target.value as 'cover' | 'contain' })
              }
            >
              <option value="cover">Cover (fill)</option>
              <option value="contain">Contain (fit)</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
              Dark overlay — {Math.round((background?.overlay ?? 0) * 100)}%
            </span>
            <input
              type="range"
              min={0}
              max={80}
              value={Math.round((background?.overlay ?? 0) * 100)}
              onChange={(e) =>
                patch({ overlay: parseInt(e.target.value, 10) / 100 })
              }
              className="w-full"
            />
          </label>
          <p className="text-xs text-muted">
            The overlay darkens the image so scores stay readable.
          </p>
        </>
      )}

      <p className="text-[11px] text-muted">
        Tip: tap a zone to edit its widget. With nothing selected, this
        panel sets the whole scoreboard's background.
      </p>
    </div>
  );
}

const FONT_LABELS: { v: FontKey; label: string }[] = [
  { v: 'default', label: 'Default (sans)' },
  { v: 'condensed', label: 'Condensed' },
  { v: 'mono', label: 'Monospace' },
  { v: 'rounded', label: 'Rounded' },
];
const FRAME_LABELS: { v: FrameStyle; label: string }[] = [
  { v: 'flat', label: 'Flat' },
  { v: 'rounded', label: 'Rounded' },
  { v: 'angular', label: 'Angular' },
  { v: 'borderless', label: 'Borderless' },
];
const DIGIT_LABELS: { v: DigitStyle; label: string }[] = [
  { v: 'segment', label: '7-Segment' },
  { v: 'bulb', label: 'LED Bulb' },
  { v: 'broadcast', label: 'Broadcast Clean' },
];
const PRESET_LABELS: { v: ThemePreset; label: string }[] = [
  { v: 'classic', label: 'Classic Red' },
  { v: 'maroon', label: 'Maroon' },
  { v: 'navy', label: 'Navy' },
  { v: 'forest', label: 'Forest' },
  { v: 'royal', label: 'Royal' },
];

function ThemeEditor({
  theme,
  onChange,
}: {
  theme: LayoutTheme | null;
  onChange: (t: LayoutTheme | null) => void;
}) {
  // Set or clear a single field; collapse to null when nothing is set so
  // the layout falls back to the display's own theme.
  function setField<K extends keyof LayoutTheme>(
    key: K,
    value: LayoutTheme[K] | undefined,
  ) {
    const next: LayoutTheme = { ...theme };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onChange(Object.keys(next).length ? next : null);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted">Theme</div>
        {theme && (
          <button
            onClick={() => onChange(null)}
            className="text-xs text-muted hover:text-text hover:underline"
          >
            Reset
          </button>
        )}
      </div>

      <label className="block">
        <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
          Color preset
        </span>
        <select
          className="input"
          value={theme?.preset ?? ''}
          onChange={(e) =>
            setField('preset', (e.target.value || undefined) as ThemePreset)
          }
        >
          <option value="">Inherit display</option>
          {PRESET_LABELS.map((p) => (
            <option key={p.v} value={p.v}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
            Font
          </span>
          <select
            className="input"
            value={theme?.font ?? 'default'}
            onChange={(e) => setField('font', e.target.value as FontKey)}
          >
            {FONT_LABELS.map((f) => (
              <option key={f.v} value={f.v}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
            Panel frame
          </span>
          <select
            className="input"
            value={theme?.frame ?? 'flat'}
            onChange={(e) => setField('frame', e.target.value as FrameStyle)}
          >
            {FRAME_LABELS.map((f) => (
              <option key={f.v} value={f.v}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
          Digit style
        </span>
        <select
          className="input"
          value={theme?.digitStyle ?? ''}
          onChange={(e) =>
            setField('digitStyle', (e.target.value || undefined) as DigitStyle)
          }
        >
          <option value="">Inherit display</option>
          {DIGIT_LABELS.map((d) => (
            <option key={d.v} value={d.v}>
              {d.label}
            </option>
          ))}
        </select>
      </label>

      <p className="text-[11px] text-muted">
        Theme overrides apply when this layout is shown on a display, on top
        of the display's own colors.
      </p>
    </div>
  );
}
